/**
 * Unit detail drawer — the full commercial record for one retail unit.
 *
 * Four tabs so the density stays navigable: Overview (who/what/where),
 * Leasing (the deal), Performance (how it trades), Operations (how it runs).
 * A vacant unit has no lease or trading history, so those tabs collapse to a
 * marketing view instead of rendering empty rows.
 */

import React, { useMemo, useState } from 'react';
import Chart from 'react-apexcharts';
import {
    X, MapPin, Navigation, Mail, FileText, Download, GitCompare,
    TrendingUp, TrendingDown, Phone, Globe, AtSign, Clock, Users,
    Wrench, Zap, Droplets, Award, AlertTriangle, ExternalLink,
} from 'lucide-react';
import { formatMoney, formatMoneyExact, formatNumber } from '../../Shared/Formatters';
import { formatDateLong, STATUS_COLORS, CATEGORY_COLORS } from '../data/mallData';

const TABS = [
    { id: 'overview', label: 'Overview' },
    { id: 'leasing', label: 'Leasing' },
    { id: 'performance', label: 'Performance' },
    { id: 'operations', label: 'Operations' },
];

/** Brand monogram — stands in for a logo asset without shipping fake brand art. */
function Monogram({ unit }) {
    const initials = (unit.brand || unit.name)
        .replace(/[^A-Za-z0-9 ]/g, '')
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w[0].toUpperCase())
        .join('');
    const color = CATEGORY_COLORS[unit.category] || '#64748B';
    return (
        <div className="mm-monogram" style={{ background: `linear-gradient(140deg, ${color}, ${color}66)` }}>
            {initials || '—'}
        </div>
    );
}

function StatusPill({ status }) {
    return (
        <span className="mm-status-pill" style={{ color: STATUS_COLORS[status], borderColor: `${STATUS_COLORS[status]}55`, background: `${STATUS_COLORS[status]}18` }}>
            <i style={{ background: STATUS_COLORS[status] }} />
            {status}
        </span>
    );
}

function KV({ label, value, hint, tone }) {
    return (
        <div className="mm-kv">
            <span className="mm-kv-label">{label}</span>
            <span className={`mm-kv-value${tone ? ` is-${tone}` : ''}`}>{value}</span>
            {hint && <span className="mm-kv-hint">{hint}</span>}
        </div>
    );
}

function Section({ title, icon, children, aside }) {
    return (
        <section className="mm-section">
            <header className="mm-section-head">
                <h4>{icon}{title}</h4>
                {aside}
            </header>
            {children}
        </section>
    );
}

function Delta({ value, suffix = '%' }) {
    if (value === null || value === undefined || isNaN(value)) return <span className="mm-delta">—</span>;
    const up = value >= 0;
    return (
        <span className={`mm-delta ${up ? 'is-up' : 'is-down'}`}>
            {up ? <TrendingUp size={13} /> : <TrendingDown size={13} />}
            {up ? '+' : ''}{Number(value).toFixed(1)}{suffix}
        </span>
    );
}

/** Rank bar: where this unit sits in its cohort, 1 = best. */
function RankBar({ rank, total, label }) {
    if (!rank || !total) return null;
    const pct = ((total - rank) / Math.max(1, total - 1)) * 100;
    const tone = pct >= 66 ? 'good' : pct >= 33 ? 'mid' : 'bad';
    return (
        <div className="mm-rankbar">
            <div className="mm-rankbar-top">
                <span>{label}</span>
                <strong>#{rank} <em>of {total}</em></strong>
            </div>
            <div className="mm-rankbar-track">
                <div className={`mm-rankbar-fill is-${tone}`} style={{ width: `${Math.max(3, pct)}%` }} />
            </div>
        </div>
    );
}

function SalesChart({ monthly, theme }) {
    const dark = theme === 'dark';
    const options = useMemo(() => ({
        chart: {
            type: 'area',
            height: 200,
            toolbar: { show: false },
            zoom: { enabled: false },
            fontFamily: 'inherit',
            background: 'transparent',
            animations: { enabled: true, easing: 'easeinout', speed: 500 },
        },
        theme: { mode: dark ? 'dark' : 'light' },
        dataLabels: { enabled: false },
        stroke: { curve: 'smooth', width: 2.5, colors: ['#3B82F6'] },
        fill: {
            type: 'gradient',
            gradient: { shadeIntensity: 1, opacityFrom: 0.42, opacityTo: 0.02, stops: [0, 100] },
            colors: ['#3B82F6'],
        },
        grid: {
            borderColor: dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
            strokeDashArray: 4,
            padding: { left: 4, right: 4, top: 0, bottom: 0 },
        },
        xaxis: {
            categories: monthly.map((m) => m.label),
            labels: { style: { fontSize: '10px' }, rotate: 0, hideOverlappingLabels: true },
            axisBorder: { show: false },
            axisTicks: { show: false },
            tooltip: { enabled: false },
        },
        yaxis: {
            labels: {
                style: { fontSize: '10px' },
                formatter: (v) => formatMoney(v),
            },
        },
        tooltip: {
            y: { formatter: (v) => formatMoneyExact(v) },
        },
        colors: ['#3B82F6'],
    }), [monthly, dark]);

    const series = useMemo(() => ([{ name: 'Net sales', data: monthly.map((m) => m.revenue) }]), [monthly]);

    return <Chart options={options} series={series} type="area" height={200} />;
}

export default function UnitDetailPanel({
    unit, theme = 'dark', onClose, onDirections, onCompare, onViewLease, onExport, onSelectUnit,
}) {
    const [tab, setTab] = useState('overview');

    if (!unit) return null;
    const { lease, performance: perf, operations: ops } = unit;
    const isVacant = unit.status === 'Vacant';
    const isComingSoon = unit.status === 'Coming Soon';

    return (
        <aside className="mm-panel" aria-label={`${unit.name} details`}>
            <header className="mm-panel-head">
                <button type="button" className="mm-panel-close" onClick={onClose} aria-label="Close panel">
                    <X size={18} />
                </button>
                <div className="mm-panel-identity">
                    <Monogram unit={unit} />
                    <div className="mm-panel-identity-text">
                        <h3>{unit.name}</h3>
                        <div className="mm-panel-meta">
                            <span className="mm-cat-chip" style={{ background: `${CATEGORY_COLORS[unit.category]}22`, color: CATEGORY_COLORS[unit.category] }}>
                                {unit.category}
                            </span>
                            <StatusPill status={unit.status} />
                        </div>
                    </div>
                </div>

                <div className="mm-panel-location">
                    <MapPin size={14} />
                    <span><strong>{unit.code}</strong> · {unit.locationDescription}</span>
                </div>

                <div className="mm-panel-topstats">
                    <div><span>GLA</span><strong>{formatNumber(unit.gla)} m²</strong></div>
                    <div><span>Zone</span><strong>{unit.zoneName}</strong></div>
                    {perf
                        ? <div><span>Sales / m²</span><strong>{formatMoney(perf.salesPerSqm)}</strong></div>
                        : <div><span>Asking rent</span><strong>{unit.askingRentPerSqm ? `${formatMoney(unit.askingRentPerSqm)}/m²` : '—'}</strong></div>}
                    <div>
                        <span>Vacancy risk</span>
                        <strong className={`is-risk-${(unit.riskLabel || '').toLowerCase()}`}>{unit.riskLabel} · {unit.vacancyRisk}</strong>
                    </div>
                </div>

                <nav className="mm-tabs" role="tablist">
                    {TABS.map((t) => (
                        <button
                            key={t.id}
                            type="button"
                            role="tab"
                            aria-selected={tab === t.id}
                            className={`mm-tab${tab === t.id ? ' is-active' : ''}`}
                            onClick={() => setTab(t.id)}
                        >
                            {t.label}
                        </button>
                    ))}
                </nav>
            </header>

            <div className="mm-panel-body">
                {tab === 'overview' && (
                    <>
                        <Section title="Unit record" icon={<MapPin size={14} />}>
                            <div className="mm-kv-grid">
                                <KV label="Trading name" value={unit.brand || '—'} />
                                <KV label="Unit code" value={unit.code} />
                                <KV label="Category" value={unit.category} />
                                <KV label="Floor" value={unit.floorName} />
                                <KV label="Court / zone" value={unit.zoneName} />
                                <KV label="Unit type" value={unit.isAnchor ? 'Anchor / big-box' : 'Inline shop'} />
                                <KV label="Gross leasable area" value={`${formatNumber(unit.gla)} m²`} />
                                <KV label="Frontage" value={`${formatNumber(Math.round(Math.max(unit.geom.w, unit.geom.d)))} m`} />
                            </div>
                        </Section>

                        {isVacant && (
                            <Section title="Marketing" icon={<AlertTriangle size={14} />}>
                                <div className="mm-callout is-warn">
                                    Vacant for {unit.vacantSinceMonths} month{unit.vacantSinceMonths === 1 ? '' : 's'}.
                                    Asking {formatMoney(unit.askingRentPerSqm)}/m² per annum
                                    — indicative base rent {formatMoney(unit.askingRentPerSqm * unit.gla)} p.a.
                                </div>
                            </Section>
                        )}

                        {isComingSoon && (
                            <Section title="Pipeline" icon={<Clock size={14} />}>
                                <div className="mm-callout is-info">
                                    Lease signed, fit-out in progress. Trading data starts after handover, so
                                    performance figures are intentionally blank.
                                </div>
                            </Section>
                        )}

                        {perf && (
                            <Section title="At a glance" icon={<Award size={14} />}>
                                <div className="mm-stat-row">
                                    <div className="mm-stat">
                                        <span>Sales (last 12m)</span>
                                        <strong>{formatMoney(perf.last12)}</strong>
                                        <Delta value={perf.yoyPct} />
                                    </div>
                                    <div className="mm-stat">
                                        <span>Footfall (30d)</span>
                                        <strong>{formatNumber(perf.footfall30d)}</strong>
                                    </div>
                                    <div className="mm-stat">
                                        <span>Share of mall sales</span>
                                        <strong>{perf.pctOfMallSales}%</strong>
                                    </div>
                                </div>
                                <SalesChart monthly={perf.monthly} theme={theme} />
                            </Section>
                        )}

                        {unit.nearby && unit.nearby.length > 0 && (
                            <Section title="Nearby complementary stores" icon={<Navigation size={14} />}>
                                <ul className="mm-nearby">
                                    {unit.nearby.map((n) => (
                                        <li key={n.id}>
                                            <button type="button" onClick={() => onSelectUnit && onSelectUnit(n.id)}>
                                                <span className="mm-cat-dot" style={{ background: CATEGORY_COLORS[n.category] }} />
                                                <span className="mm-nearby-name">{n.name}</span>
                                                <span className="mm-nearby-meta">{n.category} · {n.metres} m</span>
                                                <ExternalLink size={13} />
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </Section>
                        )}

                        <Section title="Media" icon={<FileText size={14} />}>
                            <div className="mm-media-grid">
                                {['Shopfront', 'Interior', 'Unit floor plan', '360° walkthrough'].map((label, i) => (
                                    <div key={label} className="mm-media-tile" style={{ '--i': i }}>
                                        <span>{label}</span>
                                        <em>sample</em>
                                    </div>
                                ))}
                            </div>
                            <p className="mm-note">Media slots are wired to the unit record; no asset library is connected in this demo build.</p>
                        </Section>
                    </>
                )}

                {tab === 'leasing' && (
                    lease ? (
                        <>
                            <Section title="Lease term" icon={<FileText size={14} />}>
                                <div className="mm-kv-grid">
                                    <KV label="Lease start" value={formatDateLong(lease.startDate)} />
                                    <KV label="Lease end" value={formatDateLong(lease.endDate)} />
                                    <KV label="Term" value={`${lease.termYears} years`} />
                                    <KV
                                        label="Remaining"
                                        value={`${lease.remainingMonths} months`}
                                        tone={lease.expiringSoon ? 'warn' : null}
                                        hint={lease.expiringSoon ? 'Expiring within 6 months' : null}
                                    />
                                    <KV label="Lease type" value={lease.type} />
                                    <KV label="Turnover rent" value={lease.turnoverPct ? `${lease.turnoverPct}% of gross sales` : 'Not applicable'} />
                                </div>
                            </Section>

                            <Section title="Commercial terms" icon={<Award size={14} />}>
                                <div className="mm-kv-grid">
                                    <KV label="Base rent (annual)" value={formatMoneyExact(lease.baseRentAnnual)} />
                                    <KV label="Base rent (monthly)" value={formatMoneyExact(lease.baseRentMonthly)} />
                                    <KV label="Rent per m²" value={`${formatMoneyExact(lease.rentPerSqm)} / yr`} />
                                    <KV label="Service charge (annual)" value={formatMoneyExact(lease.serviceChargeAnnual)} />
                                    <KV label="CAM per m²" value={`${formatMoneyExact(lease.camPerSqm)} / yr`} />
                                    <KV label="Security deposit" value={formatMoneyExact(lease.deposit)} />
                                </div>
                                {perf && (
                                    <div className={`mm-callout ${perf.occupancyCostRatio > 20 ? 'is-warn' : 'is-info'}`}>
                                        Occupancy cost ratio <strong>{perf.occupancyCostRatio}%</strong> of gross sales
                                        (rent + service charge ÷ trailing 12-month sales).
                                        {perf.occupancyCostRatio > 20
                                            ? ' Above the 20% comfort threshold — renewal risk.'
                                            : ' Within a sustainable range.'}
                                    </div>
                                )}
                            </Section>

                            <Section title="Key clauses" icon={<FileText size={14} />}>
                                <ul className="mm-clauses">
                                    {lease.clauses.map((c) => <li key={c}>{c}</li>)}
                                </ul>
                            </Section>

                            <Section title="Leasing contact" icon={<Mail size={14} />}>
                                <div className="mm-contact">
                                    <div className="mm-contact-avatar">{lease.manager.name.split(' ').map((w) => w[0]).join('')}</div>
                                    <div>
                                        <strong>{lease.manager.name}</strong>
                                        <span>Leasing Manager</span>
                                        <a href={`mailto:${lease.manager.email}`}>{lease.manager.email}</a>
                                        <a href={`tel:${lease.manager.phone.replace(/\s/g, '')}`}>{lease.manager.phone}</a>
                                    </div>
                                </div>
                            </Section>
                        </>
                    ) : (
                        <div className="mm-empty">
                            <AlertTriangle size={22} />
                            <p>No active lease. This unit is available — see the marketing summary on the Overview tab.</p>
                        </div>
                    )
                )}

                {tab === 'performance' && (
                    perf ? (
                        <>
                            <Section title="Revenue" icon={<TrendingUp size={14} />}>
                                <div className="mm-stat-row">
                                    <div className="mm-stat"><span>Last month</span><strong>{formatMoney(perf.lastMonth)}</strong><Delta value={perf.momPct} /></div>
                                    <div className="mm-stat"><span>Quarter to date</span><strong>{formatMoney(perf.qtd)}</strong></div>
                                    <div className="mm-stat"><span>Year to date</span><strong>{formatMoney(perf.ytd)}</strong></div>
                                    <div className="mm-stat"><span>Trailing 12m</span><strong>{formatMoney(perf.last12)}</strong><Delta value={perf.yoyPct} /></div>
                                </div>
                                <SalesChart monthly={perf.monthly} theme={theme} />
                            </Section>

                            <Section title="Trading efficiency" icon={<Award size={14} />}>
                                <div className="mm-kv-grid">
                                    <KV label="Sales per m² (12m)" value={formatMoneyExact(perf.salesPerSqm)} />
                                    <KV label="Average transaction value" value={formatMoneyExact(perf.atv)} />
                                    <KV label="Transactions (30d)" value={formatNumber(perf.transactions)} />
                                    <KV label="Footfall (30d)" value={formatNumber(perf.footfall30d)} />
                                    <KV label="Conversion rate" value={`${perf.conversionRate}%`} />
                                    <KV label="Occupancy cost ratio" value={`${perf.occupancyCostRatio}%`} tone={perf.occupancyCostRatio > 20 ? 'warn' : null} />
                                </div>
                            </Section>

                            <Section title="Benchmarking" icon={<GitCompare size={14} />}>
                                <RankBar rank={perf.categoryRank} total={perf.categoryCount} label={`Rank within ${unit.category}`} />
                                <RankBar rank={perf.mallRank} total={perf.mallCount} label="Rank across the mall" />
                                <div className="mm-bench-grid">
                                    <div>
                                        <span>vs category average</span>
                                        <strong>{formatMoneyExact(perf.categoryAvgDensity)}/m²</strong>
                                        <Delta value={perf.vsCategoryPct} />
                                    </div>
                                    <div>
                                        <span>vs mall average</span>
                                        <strong>{formatMoneyExact(perf.mallAvgDensity)}/m²</strong>
                                        <Delta value={perf.vsMallPct} />
                                    </div>
                                    <div>
                                        <span>Share of mall sales</span>
                                        <strong>{perf.pctOfMallSales}%</strong>
                                    </div>
                                </div>
                            </Section>
                        </>
                    ) : (
                        <div className="mm-empty">
                            <AlertTriangle size={22} />
                            <p>{isComingSoon ? 'Not yet trading — performance data begins after handover.' : 'No trading history for a vacant unit.'}</p>
                        </div>
                    )
                )}

                {tab === 'operations' && (
                    ops ? (
                        <>
                            <Section title="Trading hours" icon={<Clock size={14} />}>
                                <div className="mm-kv-grid">
                                    <KV label="Sunday – Wednesday" value={ops.hours.weekday} />
                                    <KV label="Thursday – Saturday" value={ops.hours.weekend} />
                                </div>
                            </Section>

                            <Section title="Contact" icon={<Phone size={14} />}>
                                <ul className="mm-contact-list">
                                    <li><Phone size={14} /><a href={`tel:${ops.phone.replace(/\s/g, '')}`}>{ops.phone}</a></li>
                                    <li><Mail size={14} /><a href={`mailto:${ops.email}`}>{ops.email}</a></li>
                                    <li><Globe size={14} /><span>{ops.website}</span></li>
                                    <li><AtSign size={14} /><span>{ops.instagram}</span></li>
                                </ul>
                            </Section>

                            <Section title="Current promotions" icon={<Award size={14} />}>
                                {ops.promotions.length ? (
                                    <ul className="mm-promos">
                                        {ops.promotions.map((p) => <li key={p}>{p}</li>)}
                                    </ul>
                                ) : <p className="mm-note">No promotions running this period.</p>}
                            </Section>

                            <Section title="Fit-out & condition" icon={<Wrench size={14} />}>
                                <div className="mm-kv-grid">
                                    <KV label="Staff on site" value={`${ops.staffCount}`} />
                                    <KV label="Last fit-out" value={formatDateLong(ops.lastFitOut)} />
                                    <KV
                                        label="Condition rating"
                                        value={`${ops.conditionRating} / 5 · ${ops.conditionLabel}`}
                                        tone={ops.conditionRating < 3.4 ? 'warn' : null}
                                    />
                                </div>
                            </Section>

                            <Section title="Utilities (monthly)" icon={<Zap size={14} />}>
                                <div className="mm-util-row">
                                    <div><Zap size={15} /><span>Electricity</span><strong>{formatNumber(ops.utilities.electricityKwh)} kWh</strong></div>
                                    <div><Droplets size={15} /><span>Water</span><strong>{formatNumber(ops.utilities.waterM3)} m³</strong></div>
                                    <div><Users size={15} /><span>Utility cost</span><strong>{formatMoneyExact(ops.utilities.costMonthly)}</strong></div>
                                </div>
                            </Section>
                        </>
                    ) : (
                        <div className="mm-empty">
                            <AlertTriangle size={22} />
                            <p>No operational record — the unit is not trading.</p>
                        </div>
                    )
                )}
            </div>

            <footer className="mm-panel-actions">
                <button type="button" className="mm-btn is-primary" onClick={() => onDirections && onDirections(unit)}>
                    <Navigation size={15} /> Get directions
                </button>
                <button type="button" className="mm-btn" onClick={() => onCompare && onCompare(unit)}>
                    <GitCompare size={15} /> Compare
                </button>
                <button
                    type="button"
                    className="mm-btn"
                    disabled={!lease}
                    onClick={() => lease && onViewLease && onViewLease(unit)}
                >
                    <FileText size={15} /> Lease doc
                </button>
                <button type="button" className="mm-btn" onClick={() => onExport && onExport(unit)}>
                    <Download size={15} /> Export
                </button>
                <a
                    className="mm-btn"
                    href={`mailto:${lease ? lease.manager.email : 'leasing@egoal-mall.ae'}?subject=${encodeURIComponent(`Enquiry — ${unit.name} (${unit.code})`)}`}
                >
                    <Mail size={15} /> Contact leasing
                </a>
            </footer>
        </aside>
    );
}
